import React from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getKnowledgeArticleIcon } from '../../constants/knowledge-base-icons';
import { helpTextRenderingSx } from '../../constants/help-typography';
import { useHelpTypography } from '../../hooks/useHelpTypography';

interface KnowledgeBaseArticleCardProps {
  articleId: string;
  title: string;
  viewDetailsLabel: string;
  onClick: () => void;
}

const KnowledgeBaseArticleCard: React.FC<KnowledgeBaseArticleCardProps> = ({
  articleId,
  title,
  viewDetailsLabel,
  onClick,
}) => {
  const theme = useTheme();
  const { fontFamily, cardTitleSx, cardLinkSx } = useHelpTypography();
  const Icon = getKnowledgeArticleIcon(articleId);

  return (
    <Card
      className="help-kb-card"
      sx={{
        height: '100%',
        borderRadius: '14px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
        fontFamily,
        ...helpTextRenderingSx,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: theme.shadows[4],
          borderColor: `${theme.palette.primary.main}40`,
        },
      }}
    >
      <CardActionArea
        onClick={onClick}
        sx={{ height: '100%', display: 'flex', alignItems: 'stretch' }}
      >
        <CardContent
          sx={{
            p: 2.5,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: `${theme.palette.primary.main}14`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 24, color: 'primary.main' }} />
          </Box>
          <Typography
            component="h3"
            className="help-kb-card-title"
            sx={{
              ...cardTitleSx,
              color: 'text.primary',
              flexGrow: 1,
              m: 0,
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'primary.main',
              mt: 0.5,
            }}
          >
            <Typography
              component="span"
              className="help-kb-card-link"
              sx={{
                ...cardLinkSx,
                color: 'inherit',
              }}
            >
              {viewDetailsLabel}
            </Typography>
            <ArrowForwardIcon
              sx={{
                fontSize: 14,
                transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
              }}
            />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default KnowledgeBaseArticleCard;
